import { mouse, touches } from 'd3-selection';
import { forEachObj } from '../../object-helpers';
import { positionIndividualTooltips } from './position-individual-tooltips';
import { getClosestPoint } from './get-closest-point';
import { TOOLTIP_MODE } from '../constants';
import { positionCombinedTooltip } from './position-combined-tooltip';

export function addTooltips(mode, graph, container, svgElem, layout, xScale) {

	let allSeries = Array.prototype.concat.apply([], graph.config.panels.map(panel => panel.config.series));

	const xData = graph.config.data.map(graph.config.xAxis.config.dataAccessor);

	const cursor = svgElem
		.append('line')
		.attr('class', 'hw_panel_graph_tooltip_cursor')
		.style('stroke-opacity', 0)
		.attr('x1', layout.plotAreaX)
		.attr('x2', layout.plotAreaX)
		.attr('y1', layout.plotAreaY)
		.attr('y2', layout.plotAreaY + layout.plotAreaHeight);

	const activePointsGroup = svgElem
		.append('g')
		.attr('class', 'hw_panel_graph_active_points')
		.style('fill-opacity', 0);

	const activePoints = {};
	const series = {};

	const isTooltipCombined = mode === TOOLTIP_MODE.COMBINED || mode === TOOLTIP_MODE.COMBINED_REVERSE;

	// Create the active point elements for each series
	allSeries.forEach(s => {
		series[s.config.name] = s;

		const ptElem = activePointsGroup
			.append('circle')
			.attr('class', `hw_panel_graph_active_point_${s.config.name}`)
			.attr('r', 6);

		if (s.config.color !== null) {
			ptElem.style('fill', s.config.color);
		}

		activePoints[s.config.name] = {
			isEnabled: false,
			elem: ptElem
		};
	});

	const tooltips = {
		mode,
		x: isTooltipCombined ? null : {},
		y: isTooltipCombined ? null : {},
		combined: isTooltipCombined ? {} : null
	};

	if (tooltips.x !== null) {

		tooltips.x.top = container
			.append('div')
			.attr('class', 'hw_panel_graph_tooltip hw_panel_graph_tooltip_x')
			.style('visibility', 'hidden');

		tooltips.x.bottom = container
			.append('div')
			.attr('class', 'hw_panel_graph_tooltip hw_panel_graph_tooltip_x')
			.style('visibility', 'hidden');

	}

	if (isTooltipCombined) {

		tooltips.combined = {
			labels: {}
		};

		tooltips.combined.container = container
			.append('div')
			.attr('class', 'hw_panel_graph_tooltip_combined')
			.style('visibility', 'hidden');

		tooltips.combined.labels.x = tooltips.combined.container
			.append('div')
			.attr('class', 'hw_panel_graph_tooltip_combined_label_x');

		tooltips.combined.labels.y = {};

		const series = mode === TOOLTIP_MODE.COMBINED_REVERSE
			? allSeries.concat([]).reverse()
			: allSeries;

		series.forEach(s => {
			const ttLine = tooltips.combined.container
				.append('div')
				.attr('class', 'hw_panel_graph_tooltip_combined_series_entry');

			const seriesName = ttLine
				.append('span')
				.attr('class', `hw_panel_graph_tooltip_combined_label_series_name hw_panel_graph_tooltip_combined_label_series_name_${s.config.name}`)
				.text(s.config.label + ': ');

			if (s.config.color !== null) {
				seriesName.style('color', s.config.color);
			}

			const seriesValue = ttLine
				.append('span')
				.attr('class', `hw_panel_graph_tooltip_combined_label_series_value hw_panel_graph_tooltip_combined_label_series_value_${s.config.name}`);

			tooltips.combined.labels.y[s.config.name] = {
				elem: seriesValue,
				isEnabled: false
			};
		});

	} else {

		// Create the tooltip boxes
		allSeries.forEach(s => {
			const ttElem = container
				.append('div')
				.attr('class', `hw_panel_graph_tooltip hw_panel_graph_tooltip_y hw_panel_graph_tooltip_y_${s.config.name}`)
				.style('visibility', 'hidden');

			if (s.config.color !== null) {
				ttElem.style('color', s.config.color);
			}

			tooltips.y[s.config.name] = {
				isEnabled: false,
				elem: ttElem
			};
		});

	}

	const eventCapturingBox = svgElem
		.append('rect')
		.attr('class', 'hw_panel_graph_event_capturing_box')
		.attr('x', layout.plotAreaX)
		.attr('y', layout.plotAreaY)
		.attr('width', layout.plotAreaWidth)
		.attr('height', layout.plotAreaHeight);

	eventCapturingBox.on('mouseover', () => {
		// Make sure the cursor is positioned before showing it. Mousemove may not
		// necessarily fire immediately after mouseover.
		const position = mouse(svgElem.node());

		updateTooltips(graph, layout, cursor, activePoints, tooltips, series, graph.config.xAxis, xScale, xData, position);
		showCursor(cursor);
		showActivePoints(activePointsGroup);

		if (tooltips.x !== null) {
			showXTooltips(tooltips.x);
		}
	});

	const hide = () => {
		hideCursor(cursor);
		hideActivePoints(activePointsGroup);
		hideTooltips(tooltips);
	};

	eventCapturingBox.on('mouseout', hide);

	eventCapturingBox.on('mousemove', () => {
		const position = mouse(svgElem.node());
		updateTooltips(graph, layout, cursor, activePoints, tooltips, series, graph.config.xAxis, xScale, xData, position);
	});

	eventCapturingBox.on('touchstart', () => {
		// Make sure the cursor is positioned before showing it. Mousemove may not
		// necessarily fire immediately after mouseover.
		const position = touches(svgElem.node())[0];

		updateTooltips(graph, layout, cursor, activePoints, tooltips, series, graph.config.xAxis, xScale, xData, position);
		showCursor(cursor);
		showActivePoints(activePointsGroup);
		// The Y tooltips are shown and hidden each time tooltips are updated, so don't need to do that here. Only X tooltips.
		if (tooltips.x !== null) {
			showXTooltips(tooltips.x);
		}
	});

	eventCapturingBox.on('touchmove', () => {
		// Make sure the cursor is positioned before showing it. Mousemove may not
		// necessarily fire immediately after mouseover.
		const position = touches(svgElem.node())[0];
		updateTooltips(graph, layout, cursor, activePoints, tooltips, series, graph.config.xAxis, xScale, xData, position);
	});

	// Expose event capturing box
	graph.eventCapturingBox = eventCapturingBox.node();

	// Expose the hide handler, so tooltips can be manually hidden in response to some external events.
	graph.hideTooltipFn = hide;

}

function showXTooltips(xTooltips) {
	xTooltips.top.style('visibility', '');
	xTooltips.bottom.style('visibility', '');
}

function hideTooltips(tooltips) {

	if (tooltips.y !== null) {
		// Hide the enabled tooltips still displayed on the screen
		forEachObj(tooltips.y, (seriesName, tooltip) => {
			if (tooltip.isEnabled) {
				tooltip.elem.style('visibility', 'hidden');
			}
		});
	}

	if (tooltips.combined !== null) {
		tooltips.combined.container.style('visibility', 'hidden');
	}

	if (tooltips.x !== null) {
		tooltips.x.top.style('visibility', 'hidden');
		tooltips.x.bottom.style('visibility', 'hidden');
	}

}

function updateTooltips(graph, layout, cursor, activePoints, tooltips, series, xAxis, xScale, xData, position) {

	if (position[0] !== graph.lastActivePositionX) {

		// todo handle no closest point
		const closestPoint = getClosestPoint(graph, xScale, xData, position);

		if (tooltips.y !== null) {
			// Toggle tooltip elements on and off depending on their isEnabled state
			forEachObj(tooltips.y, (name, tooltip) => {
				tooltip.isEnabled = series[name].config.dataAccessor(closestPoint.value) !== null;
				tooltip.elem.style('visibility', tooltip.isEnabled ? 'visible' : 'hidden');
			});
		}

		if (tooltips.combined !== null) {
			tooltips.combined.container.style('visibility', 'visible');

			// Toggle tooltip elements on and off depending on their isEnabled state
			forEachObj(tooltips.combined.labels.y, (name, tooltip) => {
				tooltip.isEnabled = series[name].config.dataAccessor(closestPoint.value) !== null;
			});
		}

		// Toggle active points elements on and off depending on their isEnabled state
		forEachObj(activePoints, (name, point) => {
			point.isEnabled = series[name].config.dataAccessor(closestPoint.value) !== null;
			point.elem.style('display', point.isEnabled ? '' : 'none');
		});

		updateTooltipsContent(tooltips, series, xAxis, closestPoint);
		positionTooltipComponents(graph, layout, cursor, series, activePoints, tooltips, closestPoint);

		// Keep track of the new active point index and x position to guide the process of finding
		// the closest data point the next time positioning is required.
		graph.lastActivePositionX = position[0];
		graph.lastActivePoint = closestPoint;
	}

}

function updateTooltipsContent(tooltips, series, xAxis, closestPoint) {

	if (tooltips.y !== null) {
		forEachObj(tooltips.y, (seriesName, tooltip) => {
			if (tooltip.isEnabled) {
				const formattedValue = series[seriesName].config.tooltipFormatter(closestPoint.value);
				tooltip.elem.text(formattedValue);
			}
		});
	}

	if (tooltips.combined !== null) {
		forEachObj(tooltips.combined.labels.y, (seriesName, tooltip) => {
			if (tooltip.isEnabled) {
				const formattedValue = series[seriesName].config.tooltipFormatter(closestPoint.value);
				tooltip.elem.text(formattedValue);
			} else {
				tooltip.elem.text('---');
			}
		});

		const formattedXValue = xAxis.config.tooltipFormatter(closestPoint.value);
		tooltips.combined.labels.x.text(formattedXValue);
	}

	if (tooltips.x !== null) {
		const formattedXValue = xAxis.config.tooltipFormatter(closestPoint.value);
		tooltips.x.top.text(formattedXValue);
		tooltips.x.bottom.text(formattedXValue);
	}

}

function positionTooltipComponents(graph, layout, cursor, series, activePoints, tooltips, closestPoint) {

	positionCursor(cursor, closestPoint.x);
	positionActivePoints(activePoints, series, closestPoint);

	if (tooltips.mode === TOOLTIP_MODE.COMBINED || tooltips.mode === TOOLTIP_MODE.COMBINED_REVERSE) {
		positionCombinedTooltip(layout, tooltips.combined, series, closestPoint);
	} else {
		positionIndividualTooltips(layout, tooltips, series, closestPoint);
	}

}

function showCursor(cursor) {
	cursor.style('stroke-opacity', 1);
}

function hideCursor(cursor) {
	cursor.style('stroke-opacity', 0);
}

function positionCursor(cursor, x) {
	cursor
		.attr('x1', x)
		.attr('x2', x);
}

function showActivePoints(points) {
	points.style('stroke-opacity', 1);
	points.style('fill-opacity', 1);
}

function hideActivePoints(points) {
	points.style('stroke-opacity', 0);
	points.style('fill-opacity', 0);
}

function positionActivePoints(activePoints, series, closestPoint) {

	for (let seriesName in series) {
		if (Object.prototype.hasOwnProperty.call(series, seriesName)) {

			const currentSeries = series[seriesName];
			const point = activePoints[seriesName];

			// Only move the point if it is enabled
			if (point.isEnabled) {

				const pointY = currentSeries.config.dataAccessor(closestPoint.value);

				// Area series have lower and upper data points. Return the top point (top of the area shape)
				const domainY = currentSeries.config.type === 'area'
					? pointY[1]
					: pointY;

				const y = currentSeries.associatedScale(domainY);

				point.elem.attr('cx', closestPoint.x);
				point.elem.attr('cy', y);

			}
		}
	}

}



// WEBPACK FOOTER //
// ./utils/panelgraph/functions/add-tooltips.js